/**
 * Payroll module — Phase 2 (pt.75, 2026-09-15).
 *
 * Access: platform_admin OR email in PAYROLL_EDITOR_EMAILS (Missie).
 *
 * pt.75 changes:
 *   - Settings tab: per-row inline edit + PATCH wiring
 *   - Roster tab: read-only view sourced from Tech Profiles (the retired
 *     payroll_roster collection is no longer the source of truth)
 */
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Coins, Users, Upload, AlertCircle, Pencil, Check, X, ExternalLink, FlaskConical, Plus, ChevronDown, Trash2, CalendarRange, Info, ShieldCheck, DollarSign, Archive, ArchiveRestore } from "lucide-react";

// CC-144 (2026-10-02) — shared helpers for Payroll Run list cleanup.
// Kept exported from this file (not a /lib/* module) because the
// second consumer, PayrollRunDetail.jsx, imports them from here; a
// util-module hop is unnecessary for two call sites.
export function fmtPayWeek(s) {
  // Belt-and-suspenders: the Patch-C migration normalizes legacy
  // YYYYMMDD -> YYYY-MM-DD at rest; this handles anything that slips
  // through (test runs restored from backup, pre-migration previews).
  if (!s || typeof s !== "string") return s || "—";
  const m = /^(\d{4})(\d{2})(\d{2})$/.exec(s);
  return m ? `${m[1]}-${m[2]}-${m[3]}` : s;
}
export function derivePayrollState(run) {
  if (!run) return { code: "DRAFT", label: "DRAFT", tone: "muted" };
  if (run.archived_at)
    return { code: "ARCHIVED", label: "ARCHIVED", tone: "muted" };
  const status = (run.status || "").toUpperCase();
  if (status === "PAID_EXTERNALLY")
    return { code: "PAID_OUTSIDE", label: "PAID OUTSIDE PLATFORM", tone: "blue" };
  const inv = run.approval_invalidated_at;
  const approvedLive  = run.approved_at          && (!inv || run.approved_at          > inv);
  const overrideLive  = run.approval_override_at && (!inv || run.approval_override_at > inv);
  if (overrideLive)
    return { code: "APPROVED_OVERRIDE", label: "APPROVED (override)", tone: "amber" };
  if (approvedLive)
    return { code: "APPROVED",          label: "APPROVED",            tone: "green" };
  return { code: "DRAFT", label: "DRAFT", tone: "muted" };
}

const TABS = [
  { id: "settings", label: "Settings", icon: Coins },
  { id: "roster",   label: "Roster",   icon: Users },
  { id: "runs",     label: "Runs",     icon: Upload },
];

export default function Payroll() {
  const { slug } = useParams();
  const { user } = useAuth();
  const [tab, setTab] = useState("roster");
  const [err, setErr] = useState(null);
  const whoLabel = (user && (user.name || user.email)) || "Signed-in user";
  return (
    <div className="space-y-5" data-testid="payroll-page">
      <header>
        <div className="text-[12px] uppercase tracking-[.12em] text-[color:var(--cc-ink-muted)] font-semibold" data-testid="payroll-header-user">
          {whoLabel} · Payroll · Mr. Electric of Southwest Missouri
        </div>
        <h1 className="font-display font-bold text-[26px] sm:text-[32px] tracking-tight text-[#0e1420] mt-1">
          Payroll
        </h1>
        <p className="text-[14px] text-[color:var(--cc-ink-muted)] mt-1">
          Weekly payroll computation from raw source uploads. Section-3 math with
          per-tech provisional/blocking gates. Tech data sourced from Tech Profiles.
        </p>
      </header>
      <div className="flex items-center gap-1 border-b border-[#e7e9ee]">
        {TABS.map((t) => {
          const Icon = t.icon;
          const active = tab === t.id;
          return (
            <button
              key={t.id}
              onClick={() => { setTab(t.id); setErr(null); }}
              data-testid={`payroll-tab-${t.id}`}
              className={`flex items-center gap-2 px-4 py-2.5 text-[13.5px] font-semibold border-b-2 -mb-px transition-colors ${
                active ? "border-[#0072bc] text-[#0072bc]"
                       : "border-transparent text-[color:var(--cc-ink-muted)] hover:text-[#0e1420]"
              }`}
            >
              <Icon size={15} strokeWidth={1.8} /> {t.label}
            </button>
          );
        })}
      </div>
      {err && (
        <div className="flex items-start gap-3 bg-[#fdecec] border border-[#f7c1c1] rounded-xl p-3">
          <AlertCircle size={16} className="text-[#7a1c1c] mt-0.5" />
          <div className="text-[13px] text-[#7a1c1c]" data-testid="payroll-err">{err}</div>
        </div>
      )}
      {tab === "settings" && <SettingsTab slug={slug} onErr={setErr} />}
      {tab === "roster"   && <RosterTab   slug={slug} onErr={setErr} />}
      {tab === "runs"     && <RunsTab     slug={slug} onErr={setErr} />}
    </div>
  );
}

// pt.75: settings row spec — key, label, format ('pct' | 'dollars'), optional note.
const SETTINGS_ROWS = [
  { key: "weekly_commission_goal", label: "Weekly commission goal", format: "dollars", note: "Revenue threshold for at-goal tier bump" },
  { key: "commission_pct_below_goal", label: "Commission % — below goal", format: "pct", note: "Any license, weekly revenue < goal" },
  { key: "commission_pct_non_journeyman_at_goal", label: "Commission % — non-journeyman at/above goal", format: "pct" },
  { key: "commission_pct_journeyman_at_goal", label: "Commission % — journeyman at/above goal", format: "pct" },
  { key: "commission_pct_master_always", label: "Commission % — master (always)", format: "pct", note: "Revenue-independent" },
  { key: "team_leader_bonus_pct", label: "Team Leader bonus", format: "pct", note: "Added AFTER tier lookup" },
  { key: "material_overage_allowance_pct", label: "Material overage allowance", format: "pct", note: "Anything over → deducted" },
  { key: "ntf_base_dollars", label: "NTF base $", format: "dollars", note: "× that week's commission %" },
  { key: "warranty_hourly_rate", label: "Warranty hourly rate", format: "dollars" },
  { key: "meeting_pay_dollars", label: "Meeting pay", format: "dollars", note: "3.5 hrs × $25" },
  { key: "payout_threshold_commercial", label: "Payout threshold — commercial", format: "dollars" },
  { key: "payout_threshold_residential", label: "Payout threshold — residential", format: "dollars" },
  { key: "csr_followup_threshold", label: "CSR follow-up threshold", format: "dollars" },
  { key: "csr_share_below_threshold", label: "CSR share — below threshold", format: "pct" },
  { key: "csr_share_at_threshold", label: "CSR share — at/above threshold", format: "pct" },
];

function SettingsTab({ slug, onErr }) {
  const [s, setS] = useState(null);
  const [editingKey, setEditingKey] = useState(null);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const load = async () => {
    try {
      const { data } = await api.get(`/payroll/settings?tenant_slug=${slug}`);
      setS(data);
    } catch (e) { onErr(e?.response?.data?.detail || e.message); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [slug]);
  if (!s) return <div className="text-[13px] text-[color:var(--cc-ink-muted)]">Loading…</div>;
  const fmt = (v, fmt) => v == null ? "—"
    : fmt === "pct"    ? `${(parseFloat(v) * 100).toFixed(1)}%`
    : fmt === "dollars" ? `$${parseFloat(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    : String(v);
  const startEdit = (row) => {
    setEditingKey(row.key);
    // Show raw stored value (e.g. "0.185" for pct, "7500.00" for dollars) — no format wrapping.
    setDraft(s[row.key] != null ? String(s[row.key]) : "");
  };
  const cancelEdit = () => { setEditingKey(null); setDraft(""); };
  const saveEdit = async (row) => {
    setSaving(true);
    try {
      const trimmed = draft.trim();
      // Basic client validation — server does the real check.
      if (trimmed === "" || isNaN(parseFloat(trimmed))) {
        onErr(`"${row.label}" must be a number (raw decimal for %, dollars for $ fields)`);
        setSaving(false); return;
      }
      await api.patch(`/payroll/settings?tenant_slug=${slug}`, { [row.key]: trimmed });
      await load();
      cancelEdit();
    } catch (e) { onErr(e?.response?.data?.detail || e.message); }
    finally { setSaving(false); }
  };
  return (
    <div className="bg-white border border-[#e7e9ee] rounded-xl overflow-x-auto" data-testid="payroll-settings">
      <div className="px-5 py-3 border-b border-[#eef1f6] font-display font-semibold text-[15px] flex justify-between">
        <span>Settings (Layer 1)</span>
        <span className="text-[12px] text-[color:var(--cc-ink-muted)]">Click any row to edit</span>
      </div>
      <table className="w-full min-w-[720px] text-[14px]">
        <tbody>
          {SETTINGS_ROWS.map((row) => {
            const editing = editingKey === row.key;
            return (
              <tr key={row.key} data-testid={`payroll-settings-row-${row.key}`} className="border-b border-[#f2f4f8]">
                <td className="py-2 pl-5 pr-3 text-[13.5px]">{row.label}</td>
                <td className="py-2 pr-3 font-mono-data text-right">
                  {editing ? (
                    <input data-testid={`payroll-settings-input-${row.key}`}
                      autoFocus className="border border-[#0072bc] rounded px-2 py-1 text-[13px] w-[110px] text-right font-mono-data"
                      value={draft} onChange={(e) => setDraft(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") saveEdit(row);
                        if (e.key === "Escape") cancelEdit();
                      }} />
                  ) : fmt(s[row.key], row.format)}
                </td>
                <td className="py-2 px-3 text-[12px] text-[color:var(--cc-ink-muted)] w-[45%]">{row.note}</td>
                <td className="py-2 pr-5 w-[100px] text-right">
                  {editing ? (
                    <div className="flex items-center gap-1 justify-end">
                      <button data-testid={`payroll-settings-save-${row.key}`}
                        disabled={saving} onClick={() => saveEdit(row)}
                        className="text-[#0e8a68] hover:bg-[#e6f9f2] p-1 rounded disabled:opacity-50">
                        <Check size={15} strokeWidth={2.2} />
                      </button>
                      <button data-testid={`payroll-settings-cancel-${row.key}`}
                        onClick={cancelEdit}
                        className="text-[#7a1c1c] hover:bg-[#fdecec] p-1 rounded">
                        <X size={15} strokeWidth={2.2} />
                      </button>
                    </div>
                  ) : (
                    <button data-testid={`payroll-settings-edit-${row.key}`}
                      onClick={() => startEdit(row)}
                      className="text-[color:var(--cc-ink-muted)] hover:text-[#0072bc] p-1 rounded">
                      <Pencil size={13} strokeWidth={1.8} />
                    </button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <div className="px-5 py-3 text-[12px] text-[color:var(--cc-ink-muted)] border-t border-[#eef1f6]">
        Last update: {s.updated_at} by {s.updated_by || "—"}.
      </div>
    </div>
  );
}

function RosterTab({ slug, onErr }) {
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState(null);
  useEffect(() => {
    api.get(`/payroll/roster?tenant_slug=${slug}`)
      .then((r) => { setRows(r.data.rows || []); setMeta({ as_of: r.data.as_of, source: r.data.source }); })
      .catch((e) => onErr(e?.response?.data?.detail || e.message));
  }, [slug, onErr]);
  return (
    <div className="bg-white border border-[#e7e9ee] rounded-xl overflow-x-auto" data-testid="payroll-roster">
      <div className="px-5 py-3 border-b border-[#eef1f6] font-display font-semibold text-[15px] flex justify-between items-center">
        <span>Roster · {rows.length}</span>
        <div className="flex items-center gap-3 text-[12px]">
          <span className="text-[color:var(--cc-ink-muted)]">
            Sourced from Tech Profiles · as of {meta?.as_of || "today"}
          </span>
          <Link to="/admin/tech-profiles" data-testid="payroll-roster-edit-link"
            className="inline-flex items-center gap-1 text-[#0072bc] hover:underline font-semibold">
            Edit in Tech Profiles <ExternalLink size={12} />
          </Link>
        </div>
      </div>
      <table className="w-full min-w-[880px] text-[14px]">
        <thead className="bg-[#f5f6f8] text-[11.5px] uppercase tracking-[.09em] text-[color:var(--cc-ink-muted)]">
          <tr>
            <th className="text-left py-3 px-5">Name</th>
            <th className="text-left py-3 px-5">Pay type</th>
            <th className="text-left py-3 px-5">License</th>
            <th className="text-left py-3 px-5">Team Leader</th>
            <th className="text-left py-3 px-5">Aliases</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.person_id} className="border-t border-[#eef1f6]" data-testid={`payroll-roster-row-${r.person_id}`}>
              <td className="py-3 px-5 font-semibold">{r.name}</td>
              <td className="py-3 px-5">{r.pay_type}{r.hourly_rate ? ` · $${r.hourly_rate}/hr` : ""}</td>
              <td className="py-3 px-5">
                {r.license_level || <span className="text-[#b45309]">— pending</span>}
              </td>
              <td className="py-3 px-5">
                {r.team_leader
                  ? <span className="bg-[#e7f4fb] text-[#1c6a99] rounded px-2 py-0.5 text-[11.5px] font-semibold" data-testid={`payroll-roster-tl-${r.person_id}`}>Team Leader · +1%</span>
                  : <span className="text-[color:var(--cc-ink-muted)]">—</span>}
              </td>
              <td className="py-3 px-5 text-[12px] text-[color:var(--cc-ink-muted)] font-mono-data">{(r.aliases || []).join(", ") || "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function RunsTab({ slug, onErr }) {
  const [runs, setRuns] = useState([]);
  const [testRuns, setTestRuns] = useState([]);
  const [busy, setBusy] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [testModalOpen, setTestModalOpen] = useState(false);
  // CC-144 (2026-10-02) — "show archived" toggle. Default OFF so the
  // list stays tight; backend hides archived rows unless we pass
  // ?include_archived=1. Toggle lives in the list card footer.
  const [showArchived, setShowArchived] = useState(false);
  const load = async () => {
    try {
      const [real, test] = await Promise.all([
        api.get(`/payroll/runs?tenant_slug=${slug}${showArchived ? "&include_archived=1" : ""}`),
        api.get(`/payroll/test-runs?tenant_slug=${slug}`),
      ]);
      setRuns(real.data.rows || []);
      setTestRuns(test.data.rows || []);
    } catch (e) { onErr(e?.response?.data?.detail || e.message); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [slug, showArchived]);
  const archiveRun = async (r, e) => {
    e.stopPropagation();
    if (!window.confirm(`Archive the run for pay week ${fmtPayWeek(r.pay_week_monday)}? It will be hidden from the default list but preserved. Reversible via "Show archived".`)) return;
    try { await api.post(`/payroll/runs/${r.id}/archive?tenant_slug=${slug}`); await load(); }
    catch (err) { onErr(err?.response?.data?.detail || err.message); }
  };
  const unarchiveRun = async (r, e) => {
    e.stopPropagation();
    try { await api.post(`/payroll/runs/${r.id}/unarchive?tenant_slug=${slug}`); await load(); }
    catch (err) { onErr(err?.response?.data?.detail || err.message); }
  };
  const createRealRun = async () => {
    setMenuOpen(false);
    const ws = window.prompt("Pay-week Monday (YYYY-MM-DD)?");
    if (!ws) return;
    setBusy(true);
    try {
      await api.post(`/payroll/runs?tenant_slug=${slug}`, { pay_week_monday: ws });
      await load();
    } catch (e) { onErr(e?.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  };
  const openTestModal = () => { setMenuOpen(false); setTestModalOpen(true); };
  const deleteTestRun = async (id, label) => {
    if (!window.confirm(`Delete test run "${label}"? This is a sandbox run — deletion is safe.`)) return;
    try {
      await api.delete(`/payroll/test-runs/${id}?tenant_slug=${slug}`);
      await load();
    } catch (e) { onErr(e?.response?.data?.detail || e.message); }
  };
  return (
    <div className="space-y-5" data-testid="payroll-runs-tab">
      {/* ────────── Split "+ New Run" dropdown ────────── */}
      <div className="flex justify-end relative">
        <button
          onClick={() => setMenuOpen((v) => !v)}
          disabled={busy}
          data-testid="payroll-new-run-dropdown-btn"
          className="bg-[#0072bc] text-white text-[12.5px] font-semibold px-3 py-1.5 rounded-lg hover:bg-[#005f9c] disabled:opacity-60 flex items-center gap-1.5"
        >
          <Plus size={14} strokeWidth={2} /> New run <ChevronDown size={13} strokeWidth={2} />
        </button>
        {menuOpen && (
          <div
            className="absolute right-0 top-9 z-10 bg-white border border-[#e7e9ee] rounded-lg shadow-lg overflow-hidden w-[220px]"
            data-testid="payroll-new-run-menu"
          >
            <button
              onClick={createRealRun}
              data-testid="payroll-new-real-run-btn"
              className="w-full text-left px-4 py-2.5 text-[13px] hover:bg-[#f5f6f8] border-b border-[#eef1f6] flex items-center gap-2"
            >
              <Coins size={14} strokeWidth={1.9} className="text-[#0072bc]" />
              <div>
                <div className="font-semibold">Real pay week</div>
                <div className="text-[11.5px] text-[color:var(--cc-ink-muted)]">Persistable payroll run</div>
              </div>
            </button>
            <button
              onClick={openTestModal}
              data-testid="payroll-new-test-run-btn"
              className="w-full text-left px-4 py-2.5 text-[13px] hover:bg-[#fff5eb] flex items-center gap-2"
            >
              <FlaskConical size={14} strokeWidth={1.9} className="text-[#c96a1a]" />
              <div>
                <div className="font-semibold">Test / Sandbox</div>
                <div className="text-[11.5px] text-[color:var(--cc-ink-muted)]">Preview-only, cannot finalize</div>
              </div>
            </button>
          </div>
        )}
      </div>

      {/* ────────── REAL PAYROLL RUNS ────────── */}
      <div className="bg-white border border-[#e7e9ee] rounded-xl overflow-x-auto" data-testid="payroll-runs">
        <div className="px-5 py-3 border-b border-[#eef1f6] font-display font-semibold text-[15px] flex items-center gap-2">
          <Coins size={15} strokeWidth={1.9} className="text-[#0072bc]" />
          Real Payroll Weeks · {runs.length}
        </div>
        {runs.length === 0 ? (
          <div className="px-5 py-6 text-[13px] text-[color:var(--cc-ink-muted)]">
            No runs yet. Create a new run to begin uploading a pay week's four source files.
          </div>
        ) : (
          <table className="w-full text-[14px]">
            <thead className="bg-[#f5f6f8] text-[11.5px] uppercase tracking-[.09em] text-[color:var(--cc-ink-muted)]">
              <tr>
                <th className="text-left py-3 px-5">Pay week (Mon–Sun)</th>
                <th className="text-left py-3 px-5">State</th>
                <th className="text-left py-3 px-5">Sources uploaded</th>
                <th className="text-left py-3 px-5">Created</th>
                <th className="text-right py-3 px-5"></th>
              </tr>
            </thead>
            <tbody>
              {runs.map((r) => {
                const state = derivePayrollState(r);
                const toneCls = {
                  green: "bg-[#e6f9f2] text-[#0e6b52]",
                  amber: "bg-[#fef3d9] text-[#8a5a1a]",
                  blue:  "bg-[#e7f4fb] text-[#1c6a99]",
                  muted: "bg-[#f5f6f8] text-[color:var(--cc-ink-muted)]",
                }[state.tone];
                return (
                <tr
                  key={r.id}
                  className={`border-t border-[#eef1f6] hover:bg-[#f5f6f8] cursor-pointer ${state.code === "ARCHIVED" ? "opacity-60" : ""}`}
                  onClick={(e) => {
                    if (e.target.closest("[data-noclick]")) return;
                    window.location.href = `/${slug}/app/payroll/runs/${r.id}`;
                  }}
                  data-testid={`payroll-run-${r.pay_week_monday}`}
                >
                  <td className="py-3 px-5 font-mono-data">{fmtPayWeek(r.pay_week_monday)} → {fmtPayWeek(r.pay_week_sunday)}</td>
                  <td className="py-3 px-5">
                    <span
                      className={`inline-flex items-center gap-1 px-2 py-1 rounded font-semibold text-[11.5px] ${toneCls}`}
                      data-testid={`payroll-run-state-${r.pay_week_monday}`}
                    >
                      {(state.code === "APPROVED" || state.code === "APPROVED_OVERRIDE") && <ShieldCheck size={12} />}
                      {state.code === "PAID_OUTSIDE" && <DollarSign size={12} />}
                      {state.code === "ARCHIVED" && <Archive size={12} />}
                      {state.label}
                    </span>
                    {state.code === "APPROVED_OVERRIDE" && (
                      <div className="text-[10.5px] text-[color:var(--cc-ink-muted)] mt-0.5 font-mono-data">
                        by {r.approval_override_by || r.approved_by || "—"} · {(r.approval_override_at || "").slice(0,10)}
                      </div>
                    )}
                    {state.code === "APPROVED" && (
                      <div className="text-[10.5px] text-[color:var(--cc-ink-muted)] mt-0.5 font-mono-data">
                        by {r.approved_by || "—"} · {(r.approved_at || "").slice(0,10)}
                      </div>
                    )}
                  </td>
                  <td className="py-3 px-5 text-[12.5px] font-mono-data">{Object.keys(r.sources || {}).join(", ") || "—"}</td>
                  <td className="py-3 px-5 text-[12.5px] text-[color:var(--cc-ink-muted)]">{r.created_at}</td>
                  <td className="py-3 px-5 text-right">
                    {r.archived_at ? (
                      <button
                        data-noclick
                        onClick={(e) => unarchiveRun(r, e)}
                        data-testid={`payroll-run-unarchive-${r.pay_week_monday}`}
                        className="text-[color:var(--cc-ink-muted)] hover:text-[#0e6b52] p-1 rounded hover:bg-[#eef1f6]"
                        title="Restore to active list"
                      >
                        <ArchiveRestore size={14} strokeWidth={1.9} />
                      </button>
                    ) : (
                      <button
                        data-noclick
                        onClick={(e) => archiveRun(r, e)}
                        data-testid={`payroll-run-archive-${r.pay_week_monday}`}
                        className="text-[color:var(--cc-ink-muted)] hover:text-[#8a5a1a] p-1 rounded hover:bg-[#eef1f6]"
                        title="Archive — hide from default list"
                      >
                        <Archive size={14} strokeWidth={1.9} />
                      </button>
                    )}
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        )}
        {/* CC-144 — show-archived toggle lives in the list card footer */}
        <div className="px-5 py-2 border-t border-[#eef1f6] text-right">
          <label className="inline-flex items-center gap-1.5 text-[11.5px] text-[color:var(--cc-ink-muted)] cursor-pointer">
            <input
              type="checkbox" checked={showArchived}
              onChange={(e) => setShowArchived(e.target.checked)}
              data-testid="payroll-run-show-archived-toggle"
            />
            Show archived
          </label>
        </div>
      </div>

      {testModalOpen && (
        <NewTestRunModal
          slug={slug}
          onClose={() => setTestModalOpen(false)}
          onCreated={async () => { setTestModalOpen(false); await load(); }}
        />
      )}

      {/* NEW-TEST-RUN MODAL — closed by default */}

      {/* ────────── TEST / SANDBOX RUNS ────────── */}
      <div
        className="bg-[#fffaf3] border border-dashed border-[#e6c99a] rounded-xl overflow-x-auto"
        data-testid="payroll-test-runs-section"
      >
        <div className="px-5 py-3 border-b border-[#f0dfbe] font-display font-semibold text-[15px] flex items-center gap-2">
          <FlaskConical size={15} strokeWidth={1.9} className="text-[#c96a1a]" />
          Test / Sandbox Runs · {testRuns.length}
          <span className="ml-auto text-[11.5px] font-normal text-[#8a5a1a]">
            Preview-only · cannot finalize · auto-expires in 30 days
          </span>
        </div>
        {testRuns.length === 0 ? (
          <div className="px-5 py-6 text-[13px] text-[#8a5a1a]" data-testid="payroll-test-runs-empty">
            No test runs. Use the New run ▸ Test / Sandbox option to create one.
            Test runs let you validate the engine against synthetic CSVs without touching a real payroll week.
          </div>
        ) : (
          <table className="w-full text-[14px]">
            <thead className="bg-[#fef0d9] text-[11.5px] uppercase tracking-[.09em] text-[#8a5a1a]">
              <tr>
                <th className="text-left py-3 px-5">Label</th>
                <th className="text-left py-3 px-5">Pay week</th>
                <th className="text-left py-3 px-5">Sources uploaded</th>
                <th className="text-left py-3 px-5">Created</th>
                <th className="text-left py-3 px-5">Expires</th>
                <th className="text-right py-3 px-5"></th>
              </tr>
            </thead>
            <tbody>
              {testRuns.map((r) => (
                <tr
                  key={r.id}
                  className="border-t border-[#f0dfbe] hover:bg-[#fff5eb] cursor-pointer"
                  onClick={(e) => {
                    // don't nav when clicking the row's delete button
                    if (e.target.closest("[data-noclick]")) return;
                    window.location.href = `/${slug}/app/payroll/test-runs/${r.id}`;
                  }}
                  data-testid={`payroll-test-run-row-${r.id}`}
                >
                  <td className="py-3 px-5">
                    <span
                      className="inline-block bg-[#c96a1a] text-white text-[10px] font-bold px-1.5 py-0.5 rounded mr-2 uppercase tracking-wide"
                      data-testid="payroll-test-run-badge"
                    >
                      Test
                    </span>
                    <span className="font-semibold">{r.label}</span>
                  </td>
                  <td className="py-3 px-5 font-mono-data text-[12.5px]">
                    {fmtPayWeek(r.pay_week_monday)} → {fmtPayWeek(r.pay_week_sunday)}
                    {r.synthetic_anchor && (
                      <span className="ml-2 text-[10.5px] text-[#8a5a1a] italic">(synthetic)</span>
                    )}
                  </td>
                  <td className="py-3 px-5 text-[12.5px] font-mono-data">
                    {Object.keys(r.sources || {}).join(", ") || "—"}
                  </td>
                  <td className="py-3 px-5 text-[12.5px] text-[#8a5a1a]">{r.created_at}</td>
                  <td className="py-3 px-5 text-[12.5px] text-[#8a5a1a]">{r.expires_at}</td>
                  <td className="py-3 px-5 text-right">
                    <button
                      data-noclick
                      onClick={(e) => { e.stopPropagation(); deleteTestRun(r.id, r.label); }}
                      data-testid={`payroll-test-run-delete-${r.id}`}
                      className="text-[#8a5a1a] hover:text-[#c96a1a] p-1 rounded hover:bg-[#fef0d9]"
                      title="Delete test run"
                    >
                      <Trash2 size={14} strokeWidth={1.9} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}


// ────────────────────────────────────────────────────────────────────────────
// NewTestRunModal (pt.82, 2026-09-16)
// Test / Sandbox runs can be created with either a standard pay-week Monday
// (Mon-Sun window) OR a custom date range for historical backpay scenarios.
// Real runs deliberately do NOT get this modal — they stay Monday-only.
// ────────────────────────────────────────────────────────────────────────────
function NewTestRunModal({ slug, onClose, onCreated }) {
  const [label, setLabel] = useState("");
  const [mode, setMode] = useState("week"); // 'week' | 'range'
  const [payWeekMonday, setPayWeekMonday] = useState("");
  const [rangeStart, setRangeStart] = useState("");
  const [rangeEnd, setRangeEnd] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const rangeSpanDays = (() => {
    if (mode !== "range" || !rangeStart || !rangeEnd) return null;
    try {
      const s = new Date(rangeStart + "T00:00:00");
      const e = new Date(rangeEnd + "T00:00:00");
      return Math.round((e - s) / 86400000) + 1;
    } catch { return null; }
  })();
  const multiWeek = rangeSpanDays !== null && rangeSpanDays > 7;

  const submit = async (e) => {
    e.preventDefault();
    if (!label.trim()) { setErr("Label is required."); return; }
    setBusy(true); setErr(null);
    try {
      const body = { label: label.trim() };
      if (mode === "range") {
        if (!rangeStart || !rangeEnd) { setErr("range_start and range_end are both required."); setBusy(false); return; }
        body.range_start = rangeStart;
        body.range_end = rangeEnd;
      } else if (payWeekMonday.trim()) {
        body.pay_week_monday = payWeekMonday.trim();
      }
      await api.post(`/payroll/test-runs?tenant_slug=${slug}`, body);
      await onCreated();
    } catch (ex) {
      setErr(ex?.response?.data?.detail || ex.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-[#0e1420]/50 flex items-center justify-center p-4" data-testid="payroll-new-test-run-modal">
      <form onSubmit={submit} className="bg-white rounded-xl shadow-xl w-full max-w-[560px] overflow-hidden">
        <div className="px-5 py-4 border-b border-[#e7e9ee] flex items-center justify-between">
          <div>
            <div className="text-[11px] uppercase tracking-widest text-[#c96a1a] font-semibold flex items-center gap-1.5">
              <FlaskConical size={12} strokeWidth={2} /> Test / Sandbox run
            </div>
            <h2 className="font-display font-semibold text-[18px]">New test run</h2>
          </div>
          <button type="button" onClick={onClose} data-testid="payroll-new-test-run-close" className="p-1 hover:bg-[#f5f6f8] rounded">
            <X size={16} />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div>
            <label className="text-[12px] uppercase tracking-widest text-[color:var(--cc-ink-muted)] font-semibold block mb-1.5">Label *</label>
            <input
              data-testid="payroll-new-test-run-label"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="e.g. 9/7-9/13 parity check"
              className="w-full border border-[#e7e9ee] rounded-lg px-3 py-2 text-[13.5px] focus:border-[#0072bc] focus:outline-none"
            />
          </div>

          <div>
            <label className="text-[12px] uppercase tracking-widest text-[color:var(--cc-ink-muted)] font-semibold block mb-2">Date window</label>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setMode("week")}
                data-testid="payroll-new-test-run-mode-week"
                className={`flex-1 border rounded-lg px-3 py-2 text-[12.5px] font-semibold flex items-center justify-center gap-1.5 transition-colors ${
                  mode === "week" ? "bg-[#e7f4fb] border-[#0072bc] text-[#0072bc]" : "border-[#e7e9ee] hover:bg-[#f5f6f8]"
                }`}
              >
                <Coins size={13} strokeWidth={1.9} /> Standard pay week
              </button>
              <button
                type="button"
                onClick={() => setMode("range")}
                data-testid="payroll-new-test-run-mode-range"
                className={`flex-1 border rounded-lg px-3 py-2 text-[12.5px] font-semibold flex items-center justify-center gap-1.5 transition-colors ${
                  mode === "range" ? "bg-[#fff5eb] border-[#c96a1a] text-[#c96a1a]" : "border-[#e7e9ee] hover:bg-[#f5f6f8]"
                }`}
              >
                <CalendarRange size={13} strokeWidth={1.9} /> Custom date range
              </button>
            </div>
          </div>

          {mode === "week" ? (
            <div>
              <label className="text-[12px] uppercase tracking-widest text-[color:var(--cc-ink-muted)] font-semibold block mb-1.5">Pay-week Monday (YYYY-MM-DD)</label>
              <input
                data-testid="payroll-new-test-run-monday"
                type="date"
                value={payWeekMonday}
                onChange={(e) => setPayWeekMonday(e.target.value)}
                className="w-full border border-[#e7e9ee] rounded-lg px-3 py-2 text-[13.5px] focus:border-[#0072bc] focus:outline-none font-mono-data"
              />
              <div className="text-[11.5px] text-[color:var(--cc-ink-muted)] mt-1">
                Leave blank to use the synthetic anchor <code className="font-mono-data">2099-01-05</code>. Must be a Monday.
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[12px] uppercase tracking-widest text-[color:var(--cc-ink-muted)] font-semibold block mb-1.5">Range start</label>
                <input
                  data-testid="payroll-new-test-run-range-start"
                  type="date"
                  value={rangeStart}
                  onChange={(e) => setRangeStart(e.target.value)}
                  className="w-full border border-[#e7e9ee] rounded-lg px-3 py-2 text-[13.5px] focus:border-[#c96a1a] focus:outline-none font-mono-data"
                />
              </div>
              <div>
                <label className="text-[12px] uppercase tracking-widest text-[color:var(--cc-ink-muted)] font-semibold block mb-1.5">Range end</label>
                <input
                  data-testid="payroll-new-test-run-range-end"
                  type="date"
                  value={rangeEnd}
                  onChange={(e) => setRangeEnd(e.target.value)}
                  className="w-full border border-[#e7e9ee] rounded-lg px-3 py-2 text-[13.5px] focus:border-[#c96a1a] focus:outline-none font-mono-data"
                />
              </div>
              {rangeSpanDays !== null && (
                <div className="col-span-2 text-[11.5px] text-[#8a5a1a] font-semibold">
                  Span: {rangeSpanDays} {rangeSpanDays === 1 ? "day" : "days"}
                </div>
              )}
              {multiWeek && (
                <div
                  data-testid="payroll-new-test-run-multiweek-warning"
                  className="col-span-2 flex items-start gap-2 bg-[#e7f4fb] border border-[#c2dff0] rounded-lg px-3 py-2 text-[12px] text-[#1c6a99]"
                >
                  <Info size={13} strokeWidth={2} className="mt-0.5 flex-shrink-0" />
                  <div>
                    <b>Heads-up &mdash; multi-week range detected.</b> Test runs still compute a
                    single Mon-Sun envelope. For historical CSR backpay across a custom range
                    (weekly bucketing under the $7,500 rule per Mon-Sun), use the dedicated
                    <b> CSR Backpay </b> tool in the sidebar &mdash; it splits the range into
                    Mon-Sun blocks and evaluates the threshold per week.
                  </div>
                </div>
              )}
              <div className="col-span-2 text-[11.5px] text-[color:var(--cc-ink-muted)]">
                Both dates required &middot; ISO format &middot; <code className="font-mono-data">range_end</code> must be greater-or-equal to <code className="font-mono-data">range_start</code>.
                Custom ranges skip the Monday-weekday check.
              </div>
            </div>
          )}

          {err && (
            <div className="bg-[#fdecec] border border-[#f7c1c1] rounded-lg px-3 py-2 text-[12.5px] text-[#7a1c1c]" data-testid="payroll-new-test-run-err">
              {err}
            </div>
          )}
        </div>

        <div className="px-5 py-3 border-t border-[#e7e9ee] flex items-center justify-end gap-2 bg-[#fafbfc]">
          <button type="button" onClick={onClose} className="px-3 py-2 text-[12.5px] font-semibold rounded-lg border border-[#e7e9ee] hover:bg-[#f5f6f8]">
            Cancel
          </button>
          <button
            type="submit"
            disabled={busy}
            data-testid="payroll-new-test-run-submit"
            className="inline-flex items-center gap-1.5 px-3 py-2 text-[12.5px] font-semibold rounded-lg bg-[#c96a1a] text-white hover:bg-[#a35414] disabled:opacity-60"
          >
            <Plus size={13} strokeWidth={2} /> {busy ? "Creating…" : "Create test run"}
          </button>
        </div>
      </form>
    </div>
  );
}
